const {KitModule, registerModule} = window;

class Timeline extends KitModule {
  onInitialize() {
    // KitModule auto-maps data-js-child attributes to this.$track etc.
    if (!this.$track || !this.$sticky) return;

    this.isInViewport = false;
    this.maxTrackScroll = 0;
    this.rafId = null;

    this.$prevBtn = this.$element.querySelector('.timeline__native-nav .pagination__button.previous');
    this.$nextBtn = this.$element.querySelector('.timeline__native-nav .pagination__button.next');
    this.$fraction = this.$element.querySelector('.timeline__native-fraction');
    this.totalCards = this.$track.querySelectorAll('.timeline__card').length;

    if (this.$prevBtn && this.$nextBtn) {
      this.$prevBtn.addEventListener('click', () => this.scrollNative(-1));
      this.$nextBtn.addEventListener('click', () => this.scrollNative(1));

      this.boundOnTrackScroll = () => {
        if (!this.trackRafId) {
          this.trackRafId = requestAnimationFrame(() => {
            this.trackRafId = null;
            this.updateNativeNav();
          });
        }
      };
      this.$track.addEventListener('scroll', this.boundOnTrackScroll, {passive: true});
    }

    this.cacheDots();

    this.computeDimensions();
    this.boundOnScroll = () => this.scheduleUpdate();
    window.addEventListener('scroll', this.boundOnScroll, {passive: true});
    this.onScroll();
  }

  /**
   * Pre-calculate thresholds for progress dots to avoid parsing inline CSS on scroll.
   */
  cacheDots() {
    this.dotData = [];
    if (!this.$dots) return;

    this.$dots.forEach((dot) => {
      let threshold = 0;
      if (dot.classList.contains('timeline__progress-dot--start')) {
        threshold = 0;
      } else if (dot.classList.contains('timeline__progress-dot--end')) {
        threshold = 0.98;
      } else {
        const styleLeft = dot.style.left || dot.style.getPropertyValue('--dot-left');
        threshold = parseFloat(styleLeft) / 100 || 0;
      }
      this.dotData.push({element: dot, threshold});
    });
  }

  /** KitModule hook – fires when element enters viewport */
  onViewportEnter() {
    this.isInViewport = true;
  }

  /** KitModule hook – fires when element leaves viewport */
  onViewportExit() {
    this.isInViewport = false;
  }

  /** KitModule hook – fires on resize with built-in 250ms debounce */
  onViewportResize() {
    this.computeDimensions();
    this.onScroll();
  }

  /**
   * Cache dimensions and set section height.
   * Section height = viewport + track overflow.
   * overflow:clip hides the extra scroll area.
   */
  computeDimensions() {
    this.maxTrackScroll = this.$track.scrollWidth - this.$track.clientWidth;
    const contentHeight = this.$sticky.offsetHeight;
    const vh = window.innerHeight;

    // Center the sticky content vertically on screen (only if it fits)
    const stickyTop = Math.max(0, (vh - contentHeight) / 2);
    this.$element.style.setProperty('--timeline-sticky-top', `${stickyTop}px`);

    // Cache card dimensions for native scroll calculations to avoid layout thrashing
    const card = this.$track.querySelector('.timeline__card');
    if (card) {
      const gap = parseFloat(window.getComputedStyle(this.$track).gap) || 0;
      this.cardFullWidth = card.clientWidth + gap;
    } else {
      this.cardFullWidth = 1;
    }

    // Fallback: If the content is taller than the viewport, disable scroll-jacking
    // so the user can actually scroll down to see the bottom of the section.
    if (contentHeight > vh) {
      this.maxTrackScroll = 0;
      this.$element.style.height = 'auto'; // Remove the forced tall height
      this.$element.classList.add('is-native-scroll');
      this.$element.querySelector('.timeline__native-nav')?.removeAttribute('aria-hidden');
      this.updateNativeNav();
      return;
    }

    this.$element.classList.remove('is-native-scroll');
    this.$element.querySelector('.timeline__native-nav')?.setAttribute('aria-hidden', 'true');

    // Section height = one viewport + track scroll + 50vh buffer at the end
    // The buffer ensures the last card "settles" and remains visible for a moment before unpinning
    this.$element.style.height = `${vh + this.maxTrackScroll + (vh * 0.5)}px`;
  }

  /**
   * Throttle scroll updates to one per animation frame.
   */
  scheduleUpdate() {
    if (this.rafId || !this.isInViewport) return;
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.onScroll();
    });
  }

  /**
   * Map vertical scroll progress to horizontal track scroll.
   */
  onScroll() {
    if (this.maxTrackScroll <= 0) return;

    const scrolled = -this.$element.getBoundingClientRect().top;
    const progress = Math.max(0, Math.min(1, scrolled / this.maxTrackScroll));

    // Only scroll via JS if we are doing scroll-jacking
    if (!this.$element.classList.contains('is-native-scroll')) {
      this.$track.scrollLeft = progress * this.maxTrackScroll;
      this.updateProgress(progress);
    }
  }

  /**
   * Update progress bar fill width and dot active states.
   * @param {number} progress - Scroll progress from 0 to 1
   */
  updateProgress(progress) {
    if (this.$fill) {
      this.$fill.style.transform = `scaleX(${progress})`;
    }
    if (this.$progressBar) {
      this.$progressBar.setAttribute('aria-valuenow', Math.round(progress * 100));
    }

    if (!this.dotData) return;

    this.dotData.forEach(({element, threshold}) => {
      if (threshold === 0 && element.classList.contains('timeline__progress-dot--start')) {
        element.classList.add('is-active');
        return;
      }

      element.classList.toggle('is-active', progress >= threshold);
    });
  }
  /**
   * Scroll the native scroll wrapper left / right.
   * @param {number} direction - -1 for left or 1 for right
   */
  scrollNative(direction) {
    if (!this.cardFullWidth) return;
    this.$track.scrollBy({left: direction * this.cardFullWidth, behavior: 'smooth'});
  }

  /**
   * Update Next / Prev buttons for native scroll.
   */
  updateNativeNav() {
    if (!this.$element.classList.contains('is-native-scroll') || !this.$prevBtn) return;
    const {scrollLeft, scrollWidth, clientWidth} = this.$track;
    this.$prevBtn.disabled = scrollLeft <= 0;
    this.$nextBtn.disabled = Math.ceil(scrollLeft + clientWidth) >= scrollWidth;

    if (this.$fraction && this.totalCards > 0 && this.cardFullWidth) {
      const currentIndex = Math.min(this.totalCards, Math.round(scrollLeft / this.cardFullWidth) + 1);
      this.$fraction.innerHTML = `<span>${currentIndex}</span>&nbsp;/&nbsp;<span>${this.totalCards}</span>`;
    }
  }
}

registerModule('Timeline', Timeline);
